import { useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Search, ShoppingCart } from "lucide-react";
import Navbar from "../components/Navbar";
import FilterBar from "../components/FilterBar";
import Footer from "../components/Footer";
import api from "../lib/Urls";
import useMarketplaceFilters, {
  itemMatchesMarketplaceFilters,
} from "../hooks/useMarketplaceFilters";
import { useCart } from "../contexts/CartContext";
import { toast } from "../hooks/use-toast";

const PAGE_SIZE = 12;

const SearchPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";

  const [query, setQuery] = useState(initialQuery);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const { filters } = useMarketplaceFilters();
  const { toggleCartItem, isInCart } = useCart();

  useEffect(() => {
    setQuery(searchParams.get("q") || "");
  }, [searchParams]);

  useEffect(() => {
    const cached = sessionStorage.getItem("category_items_all");
    if (cached) {
      try {
        setItems(JSON.parse(cached));
        setLoading(false);
      } catch {
        sessionStorage.removeItem("category_items_all");
      }
    }

    api
      .get("/api/category-items")
      .then((response) => {
        const next = Array.isArray(response.data) ? response.data : [];
        setItems(next);
        sessionStorage.setItem("category_items_all", JSON.stringify(next));
      })
      .catch((error) => console.error("Failed to load marketplace items", error))
      .finally(() => setLoading(false));
  }, []);

  const results = useMemo(() => {
    const normalizedQuery = (searchParams.get("q") || "")
      .trim()
      .toLowerCase();

    return items
      .filter((item) => item.status?.toLowerCase() !== "sold")
      .filter((item) => itemMatchesMarketplaceFilters(item, filters))
      .filter((item) => {
        if (!normalizedQuery) return true;

        return [
          item.itemName,
          item.description,
          item.categoryName,
          item.type,
          item.locCountry,
          item.locCity,
          item.store,
        ]
          .filter(Boolean)
          .some((value) =>
            String(value).toLowerCase().includes(normalizedQuery)
          );
      })
      .sort((a, b) => Number(b.itemId || 0) - Number(a.itemId || 0));
  }, [items, filters, searchParams]);

  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [filters.country, filters.city, searchParams]);

  const submitSearch = (event) => {
    event.preventDefault();
    const clean = query.trim();
    setSearchParams(clean ? { q: clean } : {});
  };

  const visibleItems = results.slice(0, visibleCount);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <FilterBar />

      <main className="container mx-auto px-4 py-8">
        <div className="mx-auto max-w-3xl text-center">
          <h1 className="font-heading text-3xl font-bold text-foreground md:text-4xl">
            Search Deal<span className="text-primary">Swapify</span>
          </h1>
          <p className="mt-2 text-sm text-muted-foreground">
            Search item names, descriptions, categories and locations.
          </p>

          <form onSubmit={submitSearch} className="relative mt-6">
            <Search className="absolute left-4 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Search for phones, furniture, books..."
              className="w-full rounded-xl border border-border bg-card py-3 pl-12 pr-28 text-sm text-foreground outline-none focus:ring-1 focus:ring-primary"
              autoFocus
            />
            <button
              type="submit"
              className="absolute right-1.5 top-1.5 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
            >
              Search
            </button>
          </form>
        </div>

        <div className="mt-8 flex items-center justify-between gap-4">
          <p className="text-sm text-muted-foreground">
            {loading ? "Loading items..." : `${results.length} result${results.length === 1 ? "" : "s"}`}
          </p>
        </div>

        {!loading && results.length === 0 ? (
          <div className="mt-8 rounded-xl border border-border bg-card px-6 py-12 text-center">
            <Search className="mx-auto h-8 w-8 text-muted-foreground" />
            <h2 className="mt-3 font-semibold text-foreground">No matching items</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Try another search term or clear the location filters above.
            </p>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {visibleItems.map((item) => {
              const inCart = isInCart(item.itemId);

              return (
                <Link
                  key={item.itemId}
                  to={`/item/${item.itemId}`}
                  className="group overflow-hidden rounded-xl border border-border bg-card transition-all hover:border-primary/40 hover:shadow-[var(--glow-primary)]"
                >
                  <div className="relative h-48 overflow-hidden bg-secondary">
                    {item.imageUrl && (
                      <img
                        src={item.imageUrl}
                        alt={item.itemName}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                      />
                    )}

                    <button
                      type="button"
                      aria-label={inCart ? "Remove from cart" : "Add to cart"}
                      onClick={(event) => {
                        event.preventDefault();
                        event.stopPropagation();
                        const added = toggleCartItem(item);
                        toast({
                          title: added ? "Added to cart" : "Removed from cart",
                          description: item.itemName,
                          duration: 2500,
                        });
                      }}
                      className={`absolute right-3 top-3 rounded-lg border px-3 py-2 backdrop-blur transition-colors ${
                        inCart
                          ? "border-primary bg-primary text-primary-foreground"
                          : "border-border bg-black/70 text-yellow-400"
                      }`}
                    >
                      <ShoppingCart className="h-4 w-4" />
                    </button>
                  </div>

                  <div className="p-4">
                    <p className="text-xs capitalize text-muted-foreground">
                      {item.categoryName} · {item.type}
                    </p>
                    <h2 className="mt-1 truncate font-heading text-lg font-semibold text-foreground group-hover:text-primary">
                      {item.itemName}
                    </h2>
                    <p className="mt-1 line-clamp-2 text-sm text-muted-foreground">
                      {item.description}
                    </p>
                    <div className="mt-3 flex items-end justify-between gap-3">
                      <p className="font-semibold text-primary">
                        {item.currency} {Number(item.amount || 0).toLocaleString()}
                      </p>
                      {(item.locCity || item.locCountry) && (
                        <p className="truncate text-xs text-muted-foreground">
                          {[item.locCity, item.locCountry].filter(Boolean).join(", ")}
                        </p>
                      )}
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}

        {visibleCount < results.length && (
          <div className="mt-8 text-center">
            <button
              type="button"
              onClick={() => setVisibleCount((current) => current + PAGE_SIZE)}
              className="rounded-lg border border-border bg-secondary px-5 py-2.5 text-sm font-medium text-secondary-foreground hover:bg-muted"
            >
              Show more
            </button>
          </div>
        )}
      </main>

      <Footer />
    </div>
  );
};

export default SearchPage;
